/**
 * fetchMetricsUnfiltered.js
 * ---------------------------------------------------------------
 * Fetches the same metrics as fetchMetrics but with no demographic
 * filters applied. The result is used alongside filtered data to
 * render benchmark comparisons in the hover tooltip.
 *
 * Extracted from MapPage.fetchMetricsUnfiltered.
 *
 * @param {string}   params.level
 * @param {string}   params.constituencyCode
 * @param {function} params.getActiveGroup
 * @param {object}   params.props
 * @param {function} params.setState
 * @param {object}   params._tracking  - { inflight, lastKey, lastAt } mutated in place
 */
import { tableFromIPC } from "apache-arrow";
import { rowsFromTable } from "./helpers";
import { schemaRequestFields } from "../schemaTypes";

export async function fetchMetricsUnfiltered({ level, constituencyCode, getActiveGroup, props, setState, _tracking }) {
  const group = getActiveGroup();
  const isExplore = props.ActionMode === "explore";
  const targetSubVar1 = props?.ActionTargetSubVariables?.TargetSubVariable1;

  const resolveFilterKeys = (targetVar, _selectedSubVar) => {
    if (!targetVar) return [];
    if (targetVar.type !== "likert_grid") return targetVar.keys || [];
    // Include all sector sub-variable keys so target-area drilldown ordering
    // can rank sectors by the selected area.
    const subVarIds = targetVar.subVariables || [];
    const allKeys = subVarIds.flatMap((svId) => props.groupsById?.[svId]?.keys || []);
    return Array.from(new Set(allKeys));
  };

  const target1Keys = resolveFilterKeys(group?.TargetVariable1, targetSubVar1);
  const target2Keys = group?.TargetVariable2?.keys || [];

  if (
    !group ||
    (isExplore && !group.keys?.length) ||
    (!isExplore && !(target1Keys.length || target2Keys.length))
  ) {
    setState({ metricRowsUnfiltered: null });
    return null;
  }

  const theme = props.selectedSection || "pol";
  const keyParts = isExplore
    ? (group?.keys || []).join(",")
    : target1Keys.concat(target2Keys).join(",");
  const key = `unfiltered|${level}|${constituencyCode || "*"}|${theme}|${keyParts}`;
  const now = Date.now();

  // Unfiltered data doesn't depend on filters — cache for 30s so filter
  // changes don't trigger redundant fetches.
  if (_tracking.lastKey === key && now - _tracking.lastAt < 30_000) {
    return _tracking.lastRows ?? null;
  }
  _tracking.lastKey = key;
  _tracking.lastAt = now;

  if (_tracking.inflight) _tracking.inflight.abort();
  _tracking.inflight = new AbortController();

  const shouldAddTurnout =
    (isExplore && group?.label === "Vote intention") ||
    (!isExplore && ["targetvariable1", "targetvariable2"].some((v) => group?.[v] === "vote_intention"));

  const groupKeys = isExplore
    ? [...(group?.keys || []), ...(shouldAddTurnout ? ["pol:turnout:turnout"] : [])]
    : [
        ...target1Keys,
        ...target2Keys,
        ...(shouldAddTurnout ? ["pol:turnout:turnout"] : []),
      ];

  const requestSchema = isExplore
    ? group?.schema
    : (group?.TargetVariable1?.schema || group?.TargetVariable2?.schema || group?.schema);

  let rows = null;
  try {
    const res = await fetch(`/api/${level}_data_arrow/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        areaCodes: constituencyCode ? [constituencyCode] : [],
        theme,
        groupKeys,
        filters: {},
        ...schemaRequestFields(requestSchema),
        normalise_over_options: isExplore
          ? (group?.normalise_over_options || false)
          : (group?.TargetVariable1?.normalise_over_options || group?.TargetVariable2?.normalise_over_options || false),
      }),
      signal: _tracking.inflight.signal,
    });
    if (!res.ok) throw new Error(`unfiltered metrics ${res.status}`);
    rows = rowsFromTable(tableFromIPC(await res.arrayBuffer()));
    _tracking.lastRows = rows;
    setState({ metricRowsUnfiltered: rows });
  } catch (e) {
    if (e.name !== "AbortError") console.error("unfiltered metrics error:", e);
    setState({ metricRowsUnfiltered: null });
  } finally {
    _tracking.inflight = null;
  }
  return rows;
}
